import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios'
import { ApiCode, type ApiResult } from '@/api/types'
import { getToken, removeToken } from '@/utils/storage'
import { useOrderStore } from '@/stores/order'

// showToast / showLoadingToast / closeToast 由 unplugin-auto-import + VantResolver 自动引入（含样式）

/** 业务异常，interceptor 中抛出，便于调用方按 code 区分处理 */
export class ApiError extends Error {
  code: number

  constructor(code: number, message: string) {
    super(message)
    this.name = 'ApiError'
    this.code = code
  }
}

/** 扩展请求配置 */
declare module 'axios' {
  export interface AxiosRequestConfig {
    /** 是否显示 loading，默认 false */
    showLoading?: boolean
    /** 是否自动 toast 错误信息，默认 true */
    showError?: boolean
    /** 是否返回完整响应体（含 code / message），默认 false，只返回 data */
    returnRaw?: boolean
  }
}

let loadingCount = 0

const openLoading = () => {
  if (loadingCount === 0) {
    showLoadingToast({ message: '加载中...', forbidClick: true, duration: 0 })
  }
  loadingCount += 1
}

const closeLoading = () => {
  loadingCount = Math.max(0, loadingCount - 1)
  if (loadingCount === 0) closeToast()
}

/** 登录态失效：清理本地缓存并跳转登录页（与 router.beforeEach 保持一致的 query 构建逻辑） */
const redirectToLogin = () => {
  removeToken()
  const orderStore = useOrderStore()
  const { orderNo, pageFrom, ...rest } = orderStore.orderQuery || {}
  const query: Record<string, unknown> = pageFrom === 'offline_payment'
    ? { productId: rest.productId, orderNo }
    : { ...rest }
  const params = new URLSearchParams()
  Object.entries(query).forEach(([k, v]) => {
    if (v !== undefined && v !== null) params.set(k, String(v))
  })
  const qs = params.toString()
  const base = import.meta.env.VITE_BASE_URL || '/'
  location.replace(`${base}login${qs ? '?' + qs : ''}`)
}

const createService = (baseURL: string): AxiosInstance => {
  const instance = axios.create({
    // 开发环境走 vite proxy（前缀由 dev server 转发，绕过浏览器 CORS）
    // 生产环境同域部署时域名前缀留空，由网关按对应前缀同域转发
    baseURL,
    timeout: 15000,
    headers: { 'Content-Type': 'application/json;charset=utf-8' },
  })

  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = getToken()
      if (token) config.headers.accessToken = `${token}`
      if (config.showLoading) openLoading()
      return config
    },
    (error) => Promise.reject(error),
  )

  instance.interceptors.response.use(
    (response: AxiosResponse<ApiResult>) => {
      const config = response.config
      if (config.showLoading) closeLoading()

      // 文件流等非业务结构直接返回
      if (config.responseType === 'blob' || config.responseType === 'arraybuffer') {
        return response.data as unknown as AxiosResponse
      }

      const res = response.data
      console.log(res)
      if (res.code === ApiCode.SUCCESS) {
        // interceptor 中直接剥离业务外层结构，返回值类型由 request<T>() 收敛
        return (config.returnRaw ? res : res.data) as unknown as AxiosResponse
      }

      if (res.code === ApiCode.UNAUTHORIZED) {
        redirectToLogin()
        return Promise.reject(new ApiError(res.code, res.message || '登录已过期'))
      }

      const message = res.message || '请求失败'
      if (config.showError !== false) showToast(message)
      return Promise.reject(new ApiError(res.code, message))
    },
    (error) => {
      if (error.config?.showLoading) closeLoading()

      let message = '网络异常，请稍后重试'
      if (axios.isCancel(error)) return Promise.reject(error)

      const status = error.response?.status
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        message = '请求超时，请稍后重试'
      } else if (status === 401) {
        message = '登录已过期，请重新登录'
        redirectToLogin()
      } else if (status === 403) {
        message = '没有访问权限'
      } else if (status === 404) {
        message = '请求的资源不存在'
      } else if (status && status >= 500) {
        message = '服务器异常，请稍后重试'
      }

      if (error.config?.showError !== false) showToast(message)
      return Promise.reject(new ApiError(status ?? -1, message))
    },
  )

  return instance
}

/** 主接口实例（/cors-api） */
const service = createService(
  `${import.meta.env.VITE_API_BASE_URL || ''}${import.meta.env.VITE_API_PREFIX}`,
)

/** mkapi 接口实例（/cors-mkapi） */
const mkService = createService(
  `${import.meta.env.VITE_MKAPI_BASE_URL || ''}${import.meta.env.VITE_MKAPI_PREFIX}`,
)

/** auth 接口实例（/cors-auth） */
const authService = createService(
  `${import.meta.env.VITE_AUTH_BASE_URL || ''}${import.meta.env.VITE_AUTH_PREFIX}`,
)

/** userapi 接口实例（/cors-userapi） */
const userService = createService(
  `${import.meta.env.VITE_USERAPI_BASE_URL || ''}${import.meta.env.VITE_USERAPI_PREFIX}`,
)

/** 泛型请求方法，返回值即为后端 data 字段 */
const createRequest =
  (instance: AxiosInstance) =>
  <T = unknown>(config: AxiosRequestConfig): Promise<T> =>
    instance.request(config) as Promise<T>

const createHttp = (instance: AxiosInstance) => {
  const request = createRequest(instance)
  return {
    get: <T = unknown>(url: string, params?: object, config?: AxiosRequestConfig) =>
      request<T>({ ...config, url, params, method: 'GET' }),

    post: <T = unknown>(url: string, data?: object, config?: AxiosRequestConfig) =>
      request<T>({ ...config, url, data, method: 'POST' }),

    put: <T = unknown>(url: string, data?: object, config?: AxiosRequestConfig) =>
      request<T>({ ...config, url, data, method: 'PUT' }),

    delete: <T = unknown>(url: string, params?: object, config?: AxiosRequestConfig) =>
      request<T>({ ...config, url, params, method: 'DELETE' }),

    upload: <T = unknown>(url: string, file: File | Blob, config?: AxiosRequestConfig) => {
      const formData = new FormData()
      formData.append('file', file)
      return request<T>({
        ...config,
        url,
        data: formData,
        method: 'POST',
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    },
  }
}

export const http = createHttp(service)

/** mkapi 域名接口专用 */
export const mkHttp = createHttp(mkService)

/** auth 域名接口专用 */
export const authHttp = createHttp(authService)

/** userapi 域名接口专用 */
export const userHttp = createHttp(userService)

export default service
